import React from "react";
import moment from "moment";
import styled from "styled-components";
import { Button, Checkbox, DatePicker, Form, Input } from "../../atoms";

const ProfileFormWrapper = styled.div`
  .profile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }

  .profile-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px 24px;
  }

  .profile-footer {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
  }
`;

export interface ICustomerProfileFormValues {
  nameAndSurname: string;
  iDPassportNumber: string;
  email: string;
  phone: string;
  secondaryPhone?: string;
  dateOfBirth?: moment.Moment | null;
  newsletterOptIn?: boolean;
}

interface ICustomerProfileFormProps {
  customer: any;
  loading?: boolean;
  onSubmit: (values: ICustomerProfileFormValues) => Promise<void> | void;
  onResetPassword: () => void;
}

const parseDate = (value: any) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value;
  if (typeof value?.toDate === "function") {
    return moment(value.toDate());
  }
  return moment(value);
};

export const CustomerProfileForm: React.FC<ICustomerProfileFormProps> = ({
  customer,
  loading,
  onSubmit,
  onResetPassword,
}) => {
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (!customer) return;
    const nameAndSurname = (
      customer?.nameAndSurname ||
      `${customer?.name || ""} ${customer?.surname || ""}`
    ).trim();

    form.setFieldsValue({
      nameAndSurname,
      iDPassportNumber: customer?.iDPassportNumber,
      email: customer?.email,
      phone: customer?.phone,
      secondaryPhone: customer?.secondaryPhone,
      dateOfBirth: parseDate(customer?.dateOfBirth),
      newsletterOptIn: customer?.newsletterOptIn ?? false,
    });
  }, [customer, form]);

  const handleFinish = async (values: ICustomerProfileFormValues) => {
    await onSubmit(values);
  };

  return (
    <ProfileFormWrapper>
      <div className="profile-header">
        <div>
          <div style={{ color: "#6b7280" }}>Προφίλ</div>
          <h3 style={{ margin: 0 }}>Στοιχεία μέλους</h3>
        </div>
        <Button onClick={onResetPassword}>Reset my password</Button>
      </div>
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <div className="profile-grid">
          <Form.Item
            name="nameAndSurname"
            label="* Ονοματεπώνυμο (Name and surname)"
            rules={[
              {
                required: true,
                message: "Παρακαλώ καταχωρίστε το ονοματεπώνυμο σας",
              },
            ]}
          >
            <Input placeholder="Μάριος Σάββα" />
          </Form.Item>
          <Form.Item
            name="iDPassportNumber"
            label="* Αρ. ταυτότητας (ID number)"
            rules={[
              {
                required: true,
                message: "Παρακαλώ καταχωρίστε τον αρ. ταυτότητας",
              },
            ]}
          >
            <Input placeholder="123456" />
          </Form.Item>
          <Form.Item
            name="email"
            label="* Email address"
            rules={[
              {
                type: "email",
                message: "Παρακαλώ καταχωρίστε έγκυρο email",
              },
              {
                required: true,
                message: "Το email είναι υποχρεωτικό",
              },
            ]}
          >
            <Input placeholder="example@email.com" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="* Κυρίως τηλέφωνο (Main phone number)"
            rules={[
              {
                required: true,
                message: "Παρακαλώ καταχωρίστε το τηλέφωνο σας",
              },
            ]}
          >
            <Input placeholder="96230500" />
          </Form.Item>
          <Form.Item
            name="secondaryPhone"
            label="* Δευτερεύον τηλέφωνο (Secondary phone number)"
            rules={[
              {
                required: true,
                message: "Παρακαλώ καταχωρίστε δευτερεύον τηλέφωνο",
              },
            ]}
          >
            <Input placeholder="96230500" />
          </Form.Item>
          <Form.Item
            name="dateOfBirth"
            label="* Ημερομηνία γέννησης (Date of birth)"
            rules={[
              {
                required: true,
                message: "Παρακαλώ επιλέξτε ημερομηνία γέννησης",
              },
            ]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </div>
        <Form.Item
          name="newsletterOptIn"
          valuePropName="checked"
          style={{ marginTop: 8 }}
        >
          <Checkbox>Εγγραφή στο ενημερωτικό δελτίο</Checkbox>
        </Form.Item>
        <div className="profile-footer">
          <Button type="primary" htmlType="submit" loading={loading}>
            Save
          </Button>
        </div>
      </Form>
    </ProfileFormWrapper>
  );
};
